"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowUpOutline18, IconPaperclipOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import TextareaAutosize from "react-textarea-autosize";
import { CaddieAvatar } from "@/components/caddie/CaddieAvatar";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { VoiceInputButton } from "@/components/voice";
import { reportMicError } from "@/components/voice/reportMicError";
import { useFileUpload } from "@/hooks/useFileUpload";
import { ASK_CADDIE_LABEL } from "@/lib/caddie";
import { cn } from "@/lib/utils";
import { submitPendingPrompt } from "@/lib/workflow/submitPendingPrompt";
import type { ChatImageAttachment } from "@/types/aiChat";
import { BorderBeam } from "border-beam";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
interface DashboardPromptInputProps {
  onSubmitted?: () => void;
  autoFocus?: boolean;
}
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_IMAGES = 3;

/**
 * Dashboard Caddie prompt input — multiline text area with ASK CADDIE label
 * and mic + skeuomorphic send button, matching the Figma hero (node 2202:9123).
 * Wraps `submitPendingPrompt` so the pill + input share one hand-off path.
 * Supports drag/drop, paste, and file-picker image attachments that ride along
 * via the AI chat store (base64 for LLM, IPFS for persistence — mirrors the
 * behavior of the in-canvas AI chat panel).
 */
export function DashboardPromptInput(t0) {
  const $ = _c(90);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onSubmitted,
    autoFocus
  } = t1;
  const router = useRouter();
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [pendingImages, setPendingImages] = useState(t2);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isBeamHovered, setIsBeamHovered] = useState(false);
  const [isBeamFocused, setIsBeamFocused] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const pendingImagesRef = useRef(t3);
  const isMountedRef = useRef(true);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      maxSizeInMB: 5,
      acceptedTypes: ACCEPTED_IMAGE_TYPES
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    uploadFile: uploadImage
  } = useFileUpload(t4);
  let t5;
  let t6;
  if ($[5] !== pendingImages) {
    t5 = () => {
      pendingImagesRef.current = pendingImages;
    };
    t6 = [pendingImages];
    $[5] = pendingImages;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      isMountedRef.current = false;
      pendingImagesRef.current.forEach(_temp);
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const readFileAsDataURL = _temp2;
  let t9;
  if ($[10] !== uploadImage) {
    t9 = async files => {
      const validFiles = files.filter(_temp3).slice(0, Math.max(0, MAX_IMAGES - pendingImagesRef.current.length));
      if (validFiles.length === 0) {
        return;
      }
      const newImages = validFiles.map(_temp4);
      setPendingImages(prev => [...prev, ...newImages]);
      await Promise.allSettled(validFiles.map(async (file_1, idx) => {
        try {
          const [base64, ipfsUrl] = await Promise.all([readFileAsDataURL(file_1), uploadImage(file_1)]);
          if (!isMountedRef.current) {
            return;
          }
          setPendingImages(prev_1 => {
            const next = [...prev_1];
            const targetIdx = next.findIndex(img_1 => img_1.previewUrl === newImages[idx].previewUrl && img_1.base64 === "");
            if (targetIdx === -1) {
              return prev_1;
            }
            next[targetIdx] = {
              ...next[targetIdx],
              base64,
              ipfsUrl: ipfsUrl ?? undefined
            };
            return next;
          });
        } catch {
          if (!isMountedRef.current) {
            return;
          }
          setPendingImages(prev_0 => prev_0.filter(img_0 => img_0.previewUrl !== newImages[idx].previewUrl));
        }
      }));
    };
    $[10] = uploadImage;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const addImagesFromFiles = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = idx_0 => {
      setPendingImages(prev_2 => {
        const removed = prev_2[idx_0];
        if (removed?.previewUrl) {
          URL.revokeObjectURL(removed.previewUrl);
        }
        return prev_2.filter((_, i) => i !== idx_0);
      });
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const removeImage = t10;
  let t11;
  if ($[13] !== addImagesFromFiles) {
    t11 = e => {
      const files_0 = Array.from(e.clipboardData.files).filter(_temp5);
      if (files_0.length > 0) {
        e.preventDefault();
        addImagesFromFiles(files_0);
      }
    };
    $[13] = addImagesFromFiles;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handlePaste = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_0 => {
      e_0.preventDefault();
      setIsDraggingOver(true);
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleDragOver = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsDraggingOver(false);
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const handleDragLeave = t13;
  let t14;
  if ($[17] !== addImagesFromFiles) {
    t14 = e_1 => {
      e_1.preventDefault();
      setIsDraggingOver(false);
      const files_1 = Array.from(e_1.dataTransfer.files).filter(_temp6);
      if (files_1.length > 0) {
        addImagesFromFiles(files_1);
      }
    };
    $[17] = addImagesFromFiles;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const handleDrop = t14;
  let t15;
  if ($[19] !== addImagesFromFiles) {
    t15 = e_2 => {
      const files_2 = Array.from(e_2.target.files ?? []);
      if (files_2.length > 0) {
        addImagesFromFiles(files_2);
      }
      e_2.target.value = "";
    };
    $[19] = addImagesFromFiles;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const handleFileInputChange = t15;
  let t16;
  if ($[21] !== onSubmitted || $[22] !== router || $[23] !== value) {
    t16 = event => {
      event?.preventDefault();
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }
      setIsSubmitting(true);
      const readyImages = pendingImagesRef.current.filter(_temp7);
      submitPendingPrompt(trimmed, router, readyImages.length > 0 ? readyImages : undefined);
      onSubmitted?.();
    };
    $[21] = onSubmitted;
    $[22] = router;
    $[23] = value;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const handleSubmit = t16;
  let t17;
  if ($[25] !== handleSubmit) {
    t17 = event_0 => {
      if (event_0.key === "Enter" && !event_0.shiftKey) {
        event_0.preventDefault();
        handleSubmit();
      }
    };
    $[25] = handleSubmit;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const handleKeyDown = t17;
  let t18;
  if ($[27] !== pendingImages) {
    t18 = pendingImages.some(_temp8);
    $[27] = pendingImages;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const isUploading = t18;
  const canAttachMore = pendingImages.length < MAX_IMAGES;
  const showBeam = isBeamHovered || isBeamFocused || isDraggingOver || value.length > 0;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsBeamHovered(true);
    t20 = () => setIsBeamHovered(false);
    t21 = () => setIsBeamFocused(true);
    t22 = event_1 => {
      const nextTarget = event_1.relatedTarget;
      if (!(nextTarget instanceof Node) || !event_1.currentTarget.contains(nextTarget)) {
        setIsBeamFocused(false);
      }
    };
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    t19 = $[29];
    t20 = $[30];
    t21 = $[31];
    t22 = $[32];
  }
  const t23 = isDraggingOver && "bg-ds-brand-50/40 dark:bg-ds-brand-500/10";
  let t24;
  if ($[33] !== t23) {
    t24 = cn("bg-ds-surface-card rounded-ds-md shadow-ds-float-deep ease-out-quart relative flex flex-col gap-3 overflow-hidden border p-[15px] transition-colors duration-150", "border-ds-border-default", t23);
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== pendingImages) {
    t25 = pendingImages.length > 0 && <div className="flex flex-wrap gap-2">{pendingImages.map((img_4, idx_1) => <div key={img_4.previewUrl || idx_1} className="group relative"><img src={img_4.previewUrl || img_4.base64} alt={img_4.name || `Image ${idx_1 + 1}`} className={cn("border-ds-border-default h-14 w-14 rounded-md border object-cover", !img_4.base64 && "animate-pulse opacity-60 motion-reduce:animate-none")} /><button type="button" onClick={() => removeImage(idx_1)} className="bg-ds-neutral-800 absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-zinc-600" aria-label={`Remove image ${idx_1 + 1}`}><IconXmarkOutline18 className="h-3 w-3" /></button></div>)}</div>;
    $[35] = pendingImages;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== isDraggingOver) {
    t26 = isDraggingOver && <div className="rounded-ds-md pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 dark:bg-blue-950/40"><span className="text-sm font-medium text-blue-600 dark:text-blue-400">Drop images here</span></div>;
    $[37] = isDraggingOver;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = ACCEPTED_IMAGE_TYPES.join(",");
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== handleFileInputChange) {
    t28 = <input ref={fileInputRef} type="file" accept={t27} multiple={true} className="hidden" onChange={handleFileInputChange} />;
    $[40] = handleFileInputChange;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = e_3 => setValue(e_3.target.value);
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = cn("placeholder:text-ds-text-disabled text-ds-text-primary w-full resize-none border-0 bg-transparent p-2 text-base leading-tight outline-none");
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== autoFocus || $[45] !== handleKeyDown || $[46] !== handlePaste || $[47] !== value) {
    t31 = <TextareaAutosize ref={textareaRef} id="b3os-dashboard-prompt-input" data-voice-target="dashboard-prompt-input" minRows={1} maxRows={5} value={value} onChange={t29} onKeyDown={handleKeyDown} onPaste={handlePaste} autoFocus={autoFocus} placeholder="Build an onchain workflow with Caddie AI" className={t30} />;
    $[44] = autoFocus;
    $[45] = handleKeyDown;
    $[46] = handlePaste;
    $[47] = value;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => textareaRef.current?.focus();
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <button type="button" data-voice-target="dashboard-caddie-button" onClick={t32} className="text-ds-text-brand hover:text-ds-brand-600 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"><CaddieAvatar size={20} mood="conversation" /><span className="uppercase tracking-wider">{ASK_CADDIE_LABEL}</span></button>;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => fileInputRef.current?.click();
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  const t35 = !canAttachMore;
  const t36 = canAttachMore ? "Attach image" : `Maximum ${MAX_IMAGES} images`;
  let t37;
  let t38;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = cn("text-ds-text-muted hover:bg-ds-surface-hover hover:text-ds-neutral-700 rounded-ds-md inline-flex size-11 items-center justify-center transition-colors", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50", "disabled:cursor-not-allowed disabled:opacity-50");
    t38 = <IconPaperclipOutline18 className="size-5" />;
    $[52] = t37;
    $[53] = t38;
  } else {
    t37 = $[52];
    t38 = $[53];
  }
  let t39;
  if ($[54] !== t35 || $[55] !== t36) {
    t39 = <button type="button" onClick={t34} disabled={t35} aria-label={t36} className={t37}>{t38}</button>;
    $[54] = t35;
    $[55] = t36;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  let t40;
  if ($[57] !== value) {
    t40 = <VoiceInputButton variant="skeuomorphic" value={value} onChange={setValue} onError={reportMicError} />;
    $[57] = value;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  if ($[59] !== isSubmitting || $[60] !== isUploading || $[61] !== value) {
    t41 = !value.trim() || isSubmitting || isUploading;
    $[59] = isSubmitting;
    $[60] = isUploading;
    $[61] = value;
    $[62] = t41;
  } else {
    t41 = $[62];
  }
  const t42 = isUploading ? "Uploading images" : "Send prompt";
  let t43;
  let t44;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = cn("border-ds-border-default text-ds-text-secondary rounded-ds-md from-ds-neutral-100 to-ds-neutral-200 dark:from-ds-neutral-700 dark:to-ds-neutral-800 ease-out-quart relative inline-flex size-11 items-center justify-center border bg-gradient-to-b transition-[filter,transform,opacity] duration-150", "shadow-ds-white", "hover:brightness-[1.02] active:scale-[0.97]", "disabled:cursor-not-allowed disabled:opacity-60");
    t44 = <span aria-hidden={true} className="rounded-inherit shadow-ds-inner-light pointer-events-none absolute inset-0" />;
    $[63] = t43;
    $[64] = t44;
  } else {
    t43 = $[63];
    t44 = $[64];
  }
  let t45;
  if ($[65] !== isSubmitting || $[66] !== isUploading) {
    t45 = isSubmitting || isUploading ? <PixelLoader size="md" className="relative" /> : <IconArrowUpOutline18 className="relative size-5" />;
    $[65] = isSubmitting;
    $[66] = isUploading;
    $[67] = t45;
  } else {
    t45 = $[67];
  }
  let t46;
  if ($[68] !== t41 || $[69] !== t42 || $[70] !== t45) {
    t46 = <button type="submit" data-voice-target="dashboard-submit-prompt" disabled={t41} aria-label={t42} className={t43}>{t44}{t45}</button>;
    $[68] = t41;
    $[69] = t42;
    $[70] = t45;
    $[71] = t46;
  } else {
    t46 = $[71];
  }
  let t47;
  if ($[72] !== t39 || $[73] !== t40 || $[74] !== t46) {
    t47 = <div className="flex items-end justify-between">{t33}<div className="flex items-center gap-2">{t39}{t40}{t46}</div></div>;
    $[72] = t39;
    $[73] = t40;
    $[74] = t46;
    $[75] = t47;
  } else {
    t47 = $[75];
  }
  let t48;
  if ($[76] !== handleDrop || $[77] !== t24 || $[78] !== t25 || $[79] !== t26 || $[80] !== t28 || $[81] !== t31 || $[82] !== t47) {
    t48 = <div className={t24} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>{t25}{t26}{t28}{t31}{t47}</div>;
    $[76] = handleDrop;
    $[77] = t24;
    $[78] = t25;
    $[79] = t26;
    $[80] = t28;
    $[81] = t31;
    $[82] = t47;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== showBeam || $[85] !== t48) {
    t49 = <BorderBeam active={showBeam} borderRadius={12} brightness={1.45} colorVariant="ocean" duration={2.4} hueRange={18} saturation={1.25} size="md" strength={0.95} theme="light" className="rounded-ds-md shadow-ds-float-deep block w-full" onMouseEnter={t19} onMouseLeave={t20} onFocusCapture={t21} onBlurCapture={t22}>{t48}</BorderBeam>;
    $[84] = showBeam;
    $[85] = t48;
    $[86] = t49;
  } else {
    t49 = $[86];
  }
  let t50;
  if ($[87] !== handleSubmit || $[88] !== t49) {
    t50 = <form onSubmit={handleSubmit} data-voice-target="dashboard-prompt-form" className="border-ds-border-default bg-ds-surface-muted rounded-ds-22 w-full border p-[10px]">{t49}</form>;
    $[87] = handleSubmit;
    $[88] = t49;
    $[89] = t50;
  } else {
    t50 = $[89];
  }
  return t50;
}
function _temp8(img_3) {
  return !img_3.base64;
}
function _temp7(img_2) {
  return img_2.base64;
}
function _temp6(f_1) {
  return f_1.type.startsWith("image/");
}
function _temp5(f_0) {
  return f_0.type.startsWith("image/");
}
function _temp4(file_0) {
  return {
    base64: "",
    previewUrl: URL.createObjectURL(file_0),
    name: file_0.name
  };
}
function _temp3(f) {
  return ACCEPTED_IMAGE_TYPES.includes(f.type);
}
function _temp2(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
function _temp(img) {
  if (img.previewUrl) {
    URL.revokeObjectURL(img.previewUrl);
  }
}